/**
 * ALTCHA proof of work in the browser (F-103 §7a, public link). The API hands
 * out a signed challenge (`GET /api/public/surveys/{token}/challenge`); this
 * solves it invisibly, in Web Workers when the browser has them, and returns
 * the payload the API verifies (base64 JSON of `{ challenge, solution }`).
 * No widget and no third party: the work runs on Submit, about a second.
 */
import { solveChallenge, solveChallengeWorkers } from 'altcha-lib'
import { deriveKey } from 'altcha-lib/algorithms/web/pbkdf2'
import type { Challenge } from 'altcha-lib/types'

const TIMEOUT_MS = 60_000

const createWorker = async (): Promise<Worker> =>
  new Worker(new URL('./altchaWorker.ts', import.meta.url), { type: 'module' })

const toBase64 = (text: string): string => {
  const bytes = new TextEncoder().encode(text)
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  return window.btoa(binary)
}

/** Solves `challenge` and returns the `altcha` field for the submission. */
export const solveAltcha = async (challenge: Challenge): Promise<string> => {
  const useWorkers = typeof Worker !== 'undefined' && (navigator.hardwareConcurrency || 1) > 1
  let solution: Awaited<ReturnType<typeof solveChallenge>> | undefined
  if (useWorkers) {
    try {
      solution = await solveChallengeWorkers({
        challenge,
        concurrency: Math.min(4, navigator.hardwareConcurrency || 2),
        createWorker,
        timeout: TIMEOUT_MS
      })
    } catch {
      /* A worker that cannot start (CSP, old browser): solve on the page instead. */
      solution = undefined
    }
  }
  if (solution === undefined) solution = await solveChallenge({ challenge, deriveKey, timeout: TIMEOUT_MS })
  if (!solution) throw new Error('altcha-unsolved')
  return toBase64(JSON.stringify({ challenge: { parameters: challenge.parameters, signature: challenge.signature }, solution }))
}
